/**
 * The **panel / prompt / banner** primitives (Chassis Slice H, PRD #534 D7 /
 * ADR-0271): the app-design-system building blocks the duel handshake overlays
 * (PRD #252 Slice C / ADR-0130) established and the dice wager reused via the
 * `matchNoun` knob (ADR-0154) - extracted here so the next feature's prompt is
 * composition, not another copied style block. All are **component-owned DOM**
 * appended to `document.body` (siblings of the `#app` canvas, so they never
 * interfere with movement input) and plain-text by discipline: consumers write
 * copy with `textContent`, never `innerHTML`, so a player-controlled display
 * name can never inject markup.
 *
 * - {@link applyPanelStyle} - the centred modal card: the textured dark gump
 *   surface, engraved border stack, restrained brass accents, and Trebuchet body type.
 *   Positioned
 *   **dead-centre of the viewport** (`position: fixed` at 50%/50% with a
 *   `translate(-50%, -50%)` recentre), anchored to the viewport, never to the
 *   avatar's world position. Deliberately NOT an off-brand accent: a prompt
 *   reads as ordinary app chrome.
 * - {@link createPanelButton} - a panel button: the gold primary (the "what's
 *   new" dismiss / splash Enter look) for the affirmative action, a dark
 *   bordered plain button for the dismissive one; hover lightens each.
 * - {@link createPromptCard} - a hidden modal card (`role="dialog"`,
 *   `aria-modal`) with show/hide/isOpen; the consumer builds its body content.
 * - {@link createBanner} - the non-modal cousin: a fixed bottom-centre one-line
 *   notice (`pointer-events: none`, so it never swallows a click beneath it)
 *   with show/hide/isVisible - the duel bow prompt's chassis (ADR-0255).
 */

/** Style `el` as the centred modal card of the app's design system. */
export function applyPanelStyle(el: HTMLElement): void {
  Object.assign(el.style, {
    position: 'fixed',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    minWidth: '300px',
    maxWidth: '420px',
    padding: '20px 22px 18px',
    backgroundImage:
      "linear-gradient(rgba(5, 6, 5, 0.38), rgba(2, 3, 2, 0.72)), url('/ui/chat-texture.jpg')",
    backgroundSize: 'auto, 256px 256px',
    border: '3px solid #11120f',
    outline: '1px solid #484235',
    borderRadius: '5px',
    color: '#aaa294',
    font: "14px/1.5 'Trebuchet MS', system-ui, sans-serif",
    boxShadow:
      'inset 0 0 0 1px #343027, inset 0 0 0 3px #050605, inset 0 0 22px rgba(0, 0, 0, 0.8), 0 12px 48px rgba(0, 0, 0, 0.72)',
    zIndex: '40',
    textAlign: 'center',
  } as Partial<CSSStyleDeclaration>);
}

/**
 * Build a panel button in the app's design system: a brass primary action and a
 * muted dark dismissive action, both using the square gump control treatment.
 */
export function createPanelButton(
  doc: Document,
  label: string,
  kind: 'primary' | 'plain',
): HTMLButtonElement {
  const el = doc.createElement('button');
  el.type = 'button';
  el.textContent = label;
  const rest = kind === 'primary' ? 'rgba(38, 34, 25, 0.82)' : 'rgba(8, 8, 6, 0.62)';
  const hover = kind === 'primary' ? 'rgba(58, 50, 35, 0.92)' : 'rgba(30, 27, 21, 0.82)';
  Object.assign(el.style, {
    margin: '0 6px',
    padding: kind === 'primary' ? '8px 18px' : '7px 16px',
    borderRadius: '3px',
    border: kind === 'primary' ? '1px solid #806d49' : '1px solid #3c372c',
    background: rest,
    color: kind === 'primary' ? '#d7cda8' : '#aaa294',
    cursor: 'var(--ui-pointer-cursor, pointer)',
    font: "600 12px 'Trebuchet MS', system-ui, sans-serif",
    textTransform: 'uppercase',
    letterSpacing: '0.2px',
  } as Partial<CSSStyleDeclaration>);
  el.addEventListener('mouseenter', () => {
    el.style.background = hover;
  });
  el.addEventListener('mouseleave', () => {
    el.style.background = rest;
  });
  return el;
}

/** The handle a consumer drives a prompt card through. */
export interface PromptCard {
  /** The card element - append body content, then `show()`. */
  root: HTMLElement;
  show(): void;
  hide(): void;
  isOpen(): boolean;
}

/**
 * Build a hidden, centred modal prompt card and append it to `document.body`.
 * The consumer owns the body content and the open/close choreography (e.g. the
 * duel UI's at-most-one-open rule across its two cards).
 */
export function createPromptCard(doc: Document): PromptCard {
  const backdrop = doc.createElement('div');
  backdrop.className = 'prompt-card-backdrop';
  backdrop.hidden = true;
  Object.assign(backdrop.style, {
    position: 'fixed',
    inset: '0',
    background: 'rgba(0, 0, 0, 0.58)',
    zIndex: '39',
    pointerEvents: 'auto',
  } as Partial<CSSStyleDeclaration>);
  const root = doc.createElement('div');
  root.setAttribute('role', 'dialog');
  root.setAttribute('aria-modal', 'true');
  root.hidden = true;
  applyPanelStyle(root);
  doc.body.append(backdrop, root);
  return {
    root,
    show() {
      backdrop.hidden = false;
      root.hidden = false;
    },
    hide() {
      root.hidden = true;
      backdrop.hidden = true;
    },
    isOpen() {
      return !root.hidden;
    },
  };
}

/** What a banner instance declares. */
export interface BannerSpec {
  /** The element id (tests and CSS may look it up). */
  id: string;
  /** The one-line notice copy (textContent). */
  text: string;
  /** Distance from the viewport's bottom edge, e.g. `'96px'`. */
  bottom: string;
  /** The text colour (e.g. the duel's war-toned amber). */
  color: string;
}

/** The handle a consumer drives a banner through. */
export interface Banner {
  /** Reveal the banner (idempotent). */
  show(): void;
  /** Hide the banner (idempotent). */
  hide(): void;
  /** Whether the banner is currently visible. */
  isVisible(): boolean;
}

/**
 * Build a hidden bottom-centre notice banner and append it to `document.body`.
 * `pointer-events: none` by contract - a banner informs, it never intercepts.
 */
export function createBanner(doc: Document, spec: BannerSpec): Banner {
  const banner = doc.createElement('div');
  banner.id = spec.id;
  banner.textContent = spec.text;
  Object.assign(banner.style, {
    position: 'fixed',
    left: '50%',
    bottom: spec.bottom,
    transform: 'translateX(-50%)',
    padding: '9px 18px',
    background: 'rgba(20, 18, 14, 0.92)',
    border: '1px solid #4a4334',
    borderRadius: '9px',
    color: spec.color,
    font: "bold 15px/1.2 'Trebuchet MS', system-ui, sans-serif",
    letterSpacing: '0.02em',
    boxShadow: '0 6px 24px rgba(0, 0, 0, 0.55)',
    pointerEvents: 'none',
    userSelect: 'none',
    zIndex: '35',
    whiteSpace: 'nowrap',
  } as Partial<CSSStyleDeclaration>);
  banner.hidden = true;
  doc.body.appendChild(banner);
  return {
    show() {
      banner.hidden = false;
    },
    hide() {
      banner.hidden = true;
    },
    isVisible() {
      return !banner.hidden;
    },
  };
}
